import React, { FC, useMemo } from 'react';
import { Box, Typography, LinearProgress, Skeleton } from '@mui/material';

export interface ProgressListItem {
  title: string;
  value: number;
  subtitle?: string;
  meta?: string;
  accentClassName?: string;
  onClick?: () => void;
  customValue?: string;
}

export interface ProgressListProps {
  items?: ProgressListItem[];
  loading?: boolean;
  emptyLabel?: string;
  hideProgressBar?: boolean;
}

const COLORS = ['#0EA5E9', '#10B981', '#8B5CF6', '#F59E0B', '#EF4444', '#06B6D4'];

const ProgressList: FC<ProgressListProps> = ({
  items = [],
  loading = false,
  emptyLabel = 'No data available',
  hideProgressBar = true,
}) => {
  const maxValue = useMemo(() => {
    if (!items.length || hideProgressBar) return 1;
    return Math.max(...items.map(item => item.value || 0), 1);
  }, [items, hideProgressBar]);

  if (loading && !items.length) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {[1, 2, 3].map(i => (
          <Box key={i} sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
            <Skeleton variant="text" width="40%" height={24} sx={{ mb: 1 }} />
            <Skeleton variant="rectangular" height={8} sx={{ borderRadius: 1, mb: 1 }} />
            <Skeleton variant="text" width="60%" height={20} />
          </Box>
        ))}
      </Box>
    );
  }

  if (!items.length) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          py: 6,
          textAlign: 'center',
        }}>
        <Typography variant="body2" color="text.secondary">
          {emptyLabel}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {items.map((item, index) => {
        // Use accentClassName if it's a color hex, otherwise use COLORS array
        let color: string;
        if (item.accentClassName && typeof item.accentClassName === 'string' && item.accentClassName.startsWith('#')) {
          color = item.accentClassName;
        } else {
          color = COLORS[index % COLORS.length];
        }

        const percent = hideProgressBar ? 0 : Math.min(((item.value || 0) / maxValue) * 100, 100);
        return (
          <Box
            key={`${item.title}-${index}`}
            onClick={item.onClick}
            sx={{
              p: 2,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              backgroundColor: '#FAFBFC',
              cursor: item.onClick ? 'pointer' : 'default',
              transition: 'all 0.2s',
              '&:hover': item.onClick
                ? {
                    borderColor: color,
                    backgroundColor: '#F8F9FA',
                    boxShadow: `0 2px 8px ${color}20`,
                  }
                : {},
            }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                mb: hideProgressBar ? 0 : 1,
                gap: 2,
              }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 700,
                    color: 'text.primary',
                    mb: 0.5,
                    wordBreak: 'break-word',
                    overflowWrap: 'break-word',
                  }}>
                  {item.title}
                </Typography>
                {item.subtitle && (
                  <Typography
                    variant="caption"
                    sx={{ color: 'text.secondary', fontSize: '0.75rem', display: 'block', fontWeight: 500 }}>
                    {item.subtitle}
                  </Typography>
                )}
                {item.meta && (
                  <Typography
                    variant="caption"
                    sx={{ color: 'text.secondary', fontSize: '0.7rem', display: 'block', mt: 0.5, fontWeight: 500 }}>
                    {item.meta}
                  </Typography>
                )}
              </Box>
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: 1,
                  backgroundColor: `${color}20`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: color,
                  fontWeight: 700,
                  fontSize: '0.875rem',
                  ml: 2,
                }}>
                {item.customValue ?? (item.value || 0)}
              </Box>
            </Box>
            {!hideProgressBar && (
              <LinearProgress
                variant="determinate"
                value={percent}
                sx={{
                  height: 6,
                  borderRadius: 1,
                  backgroundColor: '#FFFFFF',
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: `${color} !important`,
                    borderRadius: 1,
                  },
                }}
              />
            )}
          </Box>
        );
      })}
    </Box>
  );
};

export default ProgressList;
